import { useCallback, useEffect, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export interface SelectableWorker {
  id: string
  name: string
  job_title: string | null
  organization_id: string
  organization_name: string
  provisioning_status: string
  chat_ready: boolean
  avatar_url: string | null
}

interface Props {
  activeWorkerId: string | null
  onSelect: (worker: SelectableWorker) => void
}

// Motivo de indisponibilidade em linguagem de operador. A instância pode estar
// provisionando (estado transitório) ou pronta mas sem bridge de chat — casos
// diferentes que pedem ações diferentes de quem está olhando.
function unavailableReason(worker: SelectableWorker): string {
  if (worker.provisioning_status === 'pending' || worker.provisioning_status === 'provisioning') {
    return 'provisionando'
  }
  return 'instância indisponível'
}

export default function AgentPicker({ activeWorkerId, onSelect }: Props) {
  const [search, setSearch] = useState('')
  const [workers, setWorkers] = useState<SelectableWorker[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  // Falha de rede/servidor precisa ser distinguível de "nenhum resultado":
  // sem agente selecionado não dá para usar o chat, então um erro silencioso
  // deixaria o operador travado sem saber que houve falha nem como tentar de novo.
  const [loadError, setLoadError] = useState(false)
  const requestRef = useRef(0)

  const load = useCallback(async (term: string) => {
    const requestId = ++requestRef.current
    setIsLoading(true)
    setLoadError(false)
    try {
      const params = new URLSearchParams()
      if (term.trim()) params.set('search', term.trim())
      const response = await fetch(`/cc/chat/workers?${params.toString()}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) throw new Error('falha ao buscar agentes')
      const payload = await response.json() as {
        workers: SelectableWorker[]
        pagination: { total: number }
      }
      // Descarta respostas fora de ordem: sem isso uma busca lenta antiga pode
      // sobrescrever o resultado da busca atual.
      if (requestRef.current !== requestId) return
      setWorkers(payload.workers)
      setTotal(payload.pagination.total)
    } catch {
      if (requestRef.current !== requestId) return
      setWorkers([])
      setTotal(0)
      setLoadError(true)
    } finally {
      if (requestRef.current === requestId) setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    const handle = setTimeout(() => { void load(search) }, search ? 250 : 0)
    return () => clearTimeout(handle)
  }, [search, load])

  return (
    <div className="flex min-h-0 flex-col gap-2" data-testid="cc-agent-picker">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="pl-8"
          aria-label="Buscar agente"
          placeholder="Buscar por agente, instância ou organização"
          data-testid="cc-agent-search"
        />
      </div>

      <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto" role="listbox" aria-label="Agentes">
        {workers.map((worker) => {
          const disabled = !worker.chat_ready
          return (
            <li key={worker.id}>
              <button
                type="button"
                role="option"
                aria-selected={worker.id === activeWorkerId}
                disabled={disabled}
                onClick={() => onSelect(worker)}
                data-testid={`cc-agent-option-${worker.id}`}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors',
                  disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-accent',
                  worker.id === activeWorkerId && 'bg-accent',
                )}
              >
                <Avatar className="size-7 shrink-0">
                  {worker.avatar_url && <AvatarImage src={worker.avatar_url} alt="" />}
                  <AvatarFallback>{worker.name.slice(0, 1).toUpperCase()}</AvatarFallback>
                </Avatar>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground">{worker.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {worker.organization_name}
                    {worker.job_title ? ` · ${worker.job_title}` : ''}
                  </span>
                </span>
                {disabled && (
                  <span className="shrink-0 text-[11px] text-muted-foreground">{unavailableReason(worker)}</span>
                )}
              </button>
            </li>
          )
        })}
      </ul>

      {!isLoading && loadError && (
        <div className="px-2 py-4 text-sm" data-testid="cc-agent-picker-error" role="alert">
          <p className="text-destructive">Não foi possível carregar os agentes.</p>
          <button
            type="button"
            onClick={() => { void load(search) }}
            className="mt-1 text-xs font-medium text-foreground underline underline-offset-2"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {!isLoading && !loadError && workers.length === 0 && (
        <p className="px-2 py-4 text-sm text-muted-foreground" data-testid="cc-agent-picker-empty">
          Nenhum agente encontrado.
        </p>
      )}
      {total > workers.length && (
        <p className="px-2 text-xs text-muted-foreground">
          Mostrando {workers.length} de {total}. Refine a busca para ver os demais.
        </p>
      )}
    </div>
  )
}
